/**
 * Canonical display strings and Phosphor icon components keyed by activity type key (kebab-case).
 */
import {
    PhBookOpen,
    PhCardsThree,
    PhChatsCircle,
    PhCheckSquareOffset,
    PhFileText,
    PhInfo,
    PhMicrophone,
    PhMonitor,
    PhPackage,
    PhPresentation,
    PhQuestion,
    PhUploadSimple,
    PhVideoCamera,
} from '@phosphor-icons/vue';

const PREFIX_BY_KEY = {
    'e-book': 'Ebook',
    'view-information': 'View information',
    video: 'Video',
    survey: 'Survey',
    quiz: 'Quiz',
    flashcard: 'Flashcards',
    'acknowledge-information': 'Acknowledge information',
    'discussion-thread': 'Discussion thread',
    podcast: 'Podcast',
    'slide-deck': 'Slide decks',
    presentation: 'Presentation',
    'file-submission': 'File submission',
    'e-learning-package': 'eLearning Package',
};

const ICON_BY_KEY = {
    'e-book': PhBookOpen,
    'view-information': PhInfo,
    video: PhVideoCamera,
    survey: PhQuestion,
    quiz: PhFileText,
    flashcard: PhCardsThree,
    'acknowledge-information': PhCheckSquareOffset,
    'discussion-thread': PhChatsCircle,
    podcast: PhMicrophone,
    'slide-deck': PhPresentation,
    presentation: PhMonitor,
    'file-submission': PhUploadSimple,
    'e-learning-package': PhPackage,
};

/**
 * @param {string|null|undefined} type
 * @returns {string}
 */
export function normalizeActivityTypeKey(type) {
    if (type == null || type === '') {
        return '';
    }

    return String(type).trim().toLowerCase().replace(/_/g, '-');
}

function titleCaseFromKebab(key) {
    if (!key) {
        return 'Activity';
    }

    return key
        .split('-')
        .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
        .join(' ');
}

/**
 * Bold-friendly prefix label (e.g. "Podcast", "Slide decks").
 * Unknown keys get Title Case from the kebab key.
 *
 * @param {string|null|undefined} activityTypeKey
 * @returns {string}
 */
export function getActivityDisplayPrefix(activityTypeKey) {
    const key = normalizeActivityTypeKey(activityTypeKey);
    if (!key) {
        return 'Activity';
    }

    if (PREFIX_BY_KEY[key]) {
        return PREFIX_BY_KEY[key];
    }

    return titleCaseFromKebab(key);
}

/**
 * Phosphor icon component for the given activity type.
 *
 * @param {string|null|undefined} activityTypeKey
 * @returns {import('vue').Component|null}
 */
export function getActivityDisplayIcon(activityTypeKey) {
    const key = normalizeActivityTypeKey(activityTypeKey);

    return ICON_BY_KEY[key] ?? null;
}
